import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm, FieldValues } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import {
  UPDATE_ENTITY_TERMINATION_OVERVIEW_CONTACTS,
  GET_ENTITY_TERMINATION_OVERVIEW,
} from '../../../utils/statApi/EntityTerminationApi';
import EntityTerminationContactsEditForm, {
  IContactsInformation,
} from './EntityTerminationContactsEditForm';

interface IEntityTerminationContactsEditPanelProps {
  closePanel: () => void;
  companyCode: string;
  contactsInfo?: IContactsInformation;
}

const mapContact = (
  contact: {
    id?: string | number;
    graphId?: string;
    text?: string;
    name?: string;
    alias?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  } | null,
) => {
  if (!contact) return null;
  return {
    id: contact.id ? Number(contact.id) : null,
    graphId: contact.graphId,
    name: contact.text || contact.name,
    alias: contact.alias,
    email: contact.email,
    firstName: contact.firstName,
    lastName: contact.lastName,
  };
};

const EntityTerminationContactsEditPanel = (
  props: IEntityTerminationContactsEditPanelProps,
): JSX.Element => {
  const { closePanel, companyCode, contactsInfo } = props;
  const contactsForm = useForm<FieldValues>({
    defaultValues: {
      ...contactsInfo,
    },
  });
  const { handleSubmit } = contactsForm;

  const [updateContacts, { loading, error }] = useMutation(
    UPDATE_ENTITY_TERMINATION_OVERVIEW_CONTACTS,
    {
      onCompleted: closePanel,
      refetchQueries: [
        {
          query: GET_ENTITY_TERMINATION_OVERVIEW,
          variables: { companyCode },
        },
      ],
    },
  );

  const handleSaveClick = (formData: IContactsInformation): void => {
    updateContacts({
      variables: {
        companyCode,
        updateOverviewContactsDto: {
          terminationContacts:
            formData.terminationContacts?.map((contact) => mapContact(contact)) ?? null,
          celaTermContact: mapContact(formData.celaTermContact ?? null),
          businessStakeholder: mapContact(formData.businessStakeholder ?? null),
          generalComments: formData.generalComments || null,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Edit Contacts"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Contacts Edit Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading}
          closePanel={closePanel}
          saveLabel="Save Contacts"
          saveTitle="Save Contacts"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        {error && <LoadingErrorMessage error={error} loading={loading} />}
        <EntityTerminationContactsEditForm form={contactsForm} contactsInfo={contactsInfo} />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationContactsEditPanel;
